import { useMemo, useState } from 'react'
import MFAccordion from '../../ui-components/MFAccordion'
import AccordionHeaderContentWithTag from '../../AccordionHeaderContentWithTag'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { MetaDataResponse, WaterfallSections, WaterfallValues } from '../../../types/mutual-fund-detail.types'
import { convertUnitAndValue, toPascalCaseWithSpaces } from '../../../utils/utils'
import { getChangeDirection } from '../../../utils/mutualFundDetail'
import RollingReturnsBody from './RRBody'
import { EventNames, UNITS } from '../../../types/misc.types'
import useAlternateISIN from '../../../hooks/useAlternateISIN'
import { trackEvent } from '../../../utils/appBridge'

const BASE_TRAILING_RETURNS_DATA: MetaDataResponse['rollingReturns'] = {
  tenure: {
    value: 0,
    period: 'YEAR'
  },
  returns: {
    unit: 'PERCENTAGE',
    value: 0
  },
  comparisonPercentage: 0
}

export default function RollingReturnsSection() {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const { data, isin } = useMetaDataContext()
  const rollingReturns = useMemo(() => {
    return data?.rollingReturns ?? BASE_TRAILING_RETURNS_DATA
  }, [data])

  const { config } = useAlternateISIN(WaterfallSections.ROLLING_RETURNS)

  const getTitle = () => {
    return `${convertUnitAndValue({
      value: rollingReturns?.tenure?.value ?? 0,
      unit: rollingReturns?.tenure?.period ?? UNITS.MONTH,
      format: {
        casing: 'PASCAL',
        type: 'SHORT'
      }
    })} Rolling Returns`
  }

  const getChangeText = () => {
    const fundBenchmark = toPascalCaseWithSpaces(data?.defaultBenchmarkFund?.name ?? '')
    const comparisonPercentage = Math.abs(rollingReturns?.comparisonPercentage ?? 0)
    return `${convertUnitAndValue({
      value: comparisonPercentage ?? 0,
      unit: UNITS.PERCENTAGE
    })} ${fundBenchmark ? `than ${fundBenchmark}` : ''}`
  }

  if (config === WaterfallValues.NO || !config) return null

  return (
    <div className='bg-white'>
      <MFAccordion
        isExpanded={isAccordionOpen}
        onAccordionClick={() =>
          setIsAccordionOpen((isAccordionOpenPrev) => {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                eventId: 'rolling_returns',
                responseName: isAccordionOpenPrev ? 'Collapse' : 'Expand',
                screenType: 'MF_L2',
                fundName: isin
              }
            })
            return !isAccordionOpenPrev
          })
        }
        icon={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/rolling-returns-icon.svg`}
        title={getTitle()}
        accessibleName={`${getTitle()}, ${convertUnitAndValue({
          value: rollingReturns?.returns?.value ?? 0,
          unit: rollingReturns?.returns?.unit ?? UNITS.PERCENTAGE
        })}, ${getChangeText()}`}
        middleContent={
          <AccordionHeaderContentWithTag
            title={getTitle()}
            value={`${convertUnitAndValue({
              value: rollingReturns?.returns?.value ?? 0,
              unit: rollingReturns?.returns?.unit ?? UNITS.PERCENTAGE
            })}`}
            changeDirection={getChangeDirection(rollingReturns?.comparisonPercentage)}
            changeText={getChangeText()}
          />
        }
        middleContentClassName='!pr-1'
        chevronTopPadding='12px'
        isBorderTop={false}
      >
        <RollingReturnsBody />
      </MFAccordion>
    </div>
  )
}
